/**
 * SUV básico (body-weight) desde tags DICOM PET.
 * Si Units ya es SUV*, se usa el valor reescalado tal cual.
 */

function dsString(image, tag) {
  if (!image || !image.data || typeof image.data.string !== 'function') return null
  try {
    const v = image.data.string(tag)
    return v != null && String(v).trim() ? String(v).trim() : null
  } catch (e) {
    return null
  }
}

function dsFloat(image, tag) {
  const s = dsString(image, tag)
  if (s == null) return null
  const n = parseFloat(s.split('\\')[0])
  return Number.isFinite(n) ? n : null
}

function parseDicomTimeToSeconds(raw) {
  if (!raw) return null
  const t = String(raw).split('.')[0]
  if (t.length < 2) return null
  const hh = parseInt(t.slice(0, 2), 10) || 0
  const mm = t.length >= 4 ? parseInt(t.slice(2, 4), 10) || 0 : 0
  const ss = t.length >= 6 ? parseInt(t.slice(4, 6), 10) || 0 : 0
  return hh * 3600 + mm * 60 + ss
}

function firstSeqItem(image, seqTag) {
  if (!image || !image.data || typeof image.data.elements !== 'object') return null
  try {
    const el = image.data.elements[seqTag]
    if (!el || !el.items || !el.items.length) return null
    return el.items[0].dataSet || el.items[0]
  } catch (e) {
    return null
  }
}

function seqFloat(item, tag) {
  if (!item) return null
  try {
    if (typeof item.floatString === 'function') {
      const n = parseFloat(item.floatString(tag))
      return Number.isFinite(n) ? n : null
    }
    if (typeof item.string === 'function') {
      const n = parseFloat(item.string(tag))
      return Number.isFinite(n) ? n : null
    }
  } catch (e) {
    /* ignore */
  }
  return null
}

function seqString(item, tag) {
  if (!item || typeof item.string !== 'function') return null
  try {
    const v = item.string(tag)
    return v != null && String(v).trim() ? String(v).trim() : null
  } catch (e) {
    return null
  }
}

export function rescalePixel(raw, image) {
  const slope = Number(image && image.slope != null ? image.slope : 1) || 1
  const intercept = Number(image && image.intercept != null ? image.intercept : 0) || 0
  return Number(raw) * slope + intercept
}

/**
 * @returns {{
 *   units: string,
 *   isSuvUnits: boolean,
 *   canComputeSuv: boolean,
 *   patientWeightKg: number|null,
 *   doseBq: number|null,
 *   halfLifeS: number|null,
 *   decayFactor: number,
 * }}
 */
export function parsePetSuvContext(image) {
  const units = (dsString(image, 'x00541001') || '').toUpperCase()
  const isSuvUnits = units.indexOf('SUV') === 0
  const patientWeightKg = dsFloat(image, 'x00101030')
  const radio = firstSeqItem(image, 'x00540016')
  const doseBq = seqFloat(radio, 'x00181074')
  const halfLifeS = seqFloat(radio, 'x00181075')
  const startTime =
    seqString(radio, 'x00181072') ||
    seqString(radio, 'x00181078') ||
    dsString(image, 'x00080032') ||
    dsString(image, 'x00080033')
  const acqTime = dsString(image, 'x00080032') || dsString(image, 'x00080033')
  const t0 = parseDicomTimeToSeconds(startTime)
  const t1 = parseDicomTimeToSeconds(acqTime)
  let decayFactor = 1
  if (halfLifeS > 0 && t0 != null && t1 != null) {
    let dt = t1 - t0
    if (dt < -12 * 3600) dt += 24 * 3600
    if (dt > 0) decayFactor = Math.exp((-Math.LN2 * dt) / halfLifeS)
  }
  const canComputeSuv =
    isSuvUnits ||
    (units === 'BQML' && patientWeightKg > 0 && doseBq > 0)
  return {
    units: units || '',
    isSuvUnits,
    canComputeSuv,
    patientWeightKg,
    doseBq,
    halfLifeS,
    decayFactor,
  }
}

/**
 * Actividad en Bq/ml o SUV nativo según Units.
 */
export function pixelToActivity(raw, image) {
  return rescalePixel(raw, image)
}

/**
 * SUV bw. Si no se puede, devuelve actividad (Bq/ml o valor nativo).
 */
export function pixelToSuv(raw, image, ctx) {
  const activity = pixelToActivity(raw, image)
  const c = ctx || parsePetSuvContext(image)
  if (c.isSuvUnits) return activity
  if (c.canComputeSuv && c.patientWeightKg > 0 && c.doseBq > 0) {
    const decayedDose = c.doseBq * (c.decayFactor > 0 ? c.decayFactor : 1)
    const weightG = c.patientWeightKg * 1000
    if (decayedDose > 0) return (activity * weightG) / decayedDose
  }
  return activity
}

export function suvUnitLabel(ctx) {
  if (!ctx) return ''
  if (ctx.isSuvUnits) return ctx.units || 'SUV'
  if (ctx.canComputeSuv) return 'SUV bw'
  if (ctx.units) return ctx.units
  return ''
}

export function formatSuv(n, digits = 2) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—'
  return n.toFixed(digits)
}

/**
 * Stats SUV sobre una lista de samples crudos.
 */
export function suvStatsFromSamples(samples, image) {
  if (!samples || !samples.length || !image) return null
  const ctx = parsePetSuvContext(image)
  let sum = 0
  let max = -Infinity
  for (let i = 0; i < samples.length; i++) {
    const v = pixelToSuv(samples[i], image, ctx)
    sum += v
    if (v > max) max = v
  }
  return {
    mean: sum / samples.length,
    max,
    n: samples.length,
    unit: suvUnitLabel(ctx) || 'SUV',
    canComputeSuv: ctx.canComputeSuv || ctx.isSuvUnits,
  }
}
